import Vue from 'vue'
import Router from 'vue-router'
import Meta from 'vue-meta'
import Login from '@/components/Login'
import NotFound from '@/components/NotFound'
import { getOnboardingStatus, isClientOnboardingRequired, onboardingRouteForStatus } from '@/utils/onboardingRouting'

import globals from '@/globals'

// Layouts
import Layout2 from '@/layout/Layout2'
import LayoutAuth from '@/layout/LayoutAuth'
import LayoutOnboarding from '@/layout/LayoutOnboarding'

Vue.use(Router)
Vue.use(Meta)

const routerPush = Router.prototype.push
const routerReplace = Router.prototype.replace

function isIgnorableNavigationFailure (failure) {
  return !failure || failure.name === 'NavigationDuplicated'
}

Router.prototype.push = function push (location, onComplete, onAbort) {
  if (onComplete || onAbort) {
    return routerPush.call(this, location, onComplete, onAbort)
  }

  return routerPush.call(this, location).catch(failure => {
    if (isIgnorableNavigationFailure(failure)) return this.currentRoute
    return Promise.reject(failure)
  })
}

Router.prototype.replace = function replace (location, onComplete, onAbort) {
  if (onComplete || onAbort) {
    return routerReplace.call(this, location, onComplete, onAbort)
  }

  return routerReplace.call(this, location).catch(failure => {
    if (isIgnorableNavigationFailure(failure)) return this.currentRoute
    return Promise.reject(failure)
  })
}

const router = new Router({
  base: '/',
  mode: 'history',
  routes: [{
    path: '/onboarding/start',
    component: LayoutOnboarding,
    meta: { guardOnboarding: true },
    children: [{
      name: 'onboarding-start',
      path: '',
      meta: { hideAiChatToggle: true },
      component: () => import('@/components/onboarding/OnboardingStart.vue')
    }]
  }, {
    path: '/',
    component: Layout2,
    meta: { guardOnboarding: true },
    children: [{
      name: 'dashboard',
      path: '',
      component: () => import('@/components/Dashboard')
    }, {
      name: 'onboarding',
      path: '/onboarding',
      meta: { hideAiChatToggle: true, stayOnAIConsentCancel: true },
      component: () => import('@/components/onboarding/Onboarding.vue')
    }, {
      name: 'profile',
      path: '/profile',
      component: () => import('@/components/Profile')
    }, {
      name: 'account',
      path: '/account',
      component: () => import('@/components/Account')
    }, {
      name: 'oauth-consent',
      path: '/oauth/consent',
      component: () => import('@/components/oauth/Consent')
    }, {
      name: 'form-insights',
      path: '/insights/form-engagement',
      component: () => import('@/components/insights/FormEngagementWrapper')
    }, {
      name: 'form-insights-for-form',
      path: '/insights/form-engagement/:id',
      meta: {redirectAfterSwitch: 'form-insights'},
      component: () => import('@/components/insights/FormEngagementWrapper')
    }, {
      name: 'field-metrics',
      path: '/insights/field-metrics',
      component: () => import('@/components/insights/FieldMetricsWrapper')
    }, {
      name: 'field-metrics-for-form',
      path: '/insights/field-metrics/:id',
      meta: {redirectAfterSwitch: 'field-metrics'},
      component: () => import('@/components/insights/FieldMetricsWrapper')
    }, {
      name: 'score',
      path: '/insights/score',
      component: () => import('@/components/insights/Score')
    }, {
      name: 'score-prefilled',
      path: '/insights/score/:url',
      meta: {redirectAfterSwitch: 'score'},
      component: () => import('@/components/insights/Score')
    }, {
      name: 'session-replays',
      path: '/insights/session-replays',
      component: () => import('@/components/insights/SessionReplay.vue')
    }, {
      name: 'session-replays-for-form',
      path: '/insights/session-replays/:id',
      meta: {redirectAfterSwitch: 'session-replays'},
      component: () => import('@/components/insights/SessionReplay.vue')
    }, {
      name: 'industry-benchmarks',
      path: '/insights/industry-benchmarks',
      component: () => import('@/components/insights/IndustryBenchmarksWrapper.vue')
    }, {
      name: 'industry-benchmarks-for-form',
      path: '/insights/industry-benchmarks/:id',
      meta: {redirectAfterSwitch: 'industry-benchmarks'},
      component: () => import('@/components/insights/IndustryBenchmarksWrapper.vue')
    }, {
      name: 'eureka',
      path: '/insights/eureka',
      component: () => import('@/components/insights/eureka/AnalysisSessions.vue')
    }, {
      name: 'eureka-analysis',
      path: '/insights/eureka/:analysisId',
      component: () => import('@/components/insights/eureka/AnalysisSessions.vue')
    }, {
      name: 'knowledge-base-ai',
      path: '/support/knowledge-base',
      component: () => import('@/components/KnowledgeBaseChat.vue'),
      meta: { hideAiChatToggle: true }
    }, {
      name: 'tags',
      path: '/setup/tags',
      component: () => import('@/components/setup/TagList')
    }, {
      name: 'tags-add',
      path: '/setup/tags/add/:type',
      component: () => import('@/components/setup/TagForm')
    }, {
      name: 'tags-edit',
      path: '/setup/tags/:id',
      meta: {redirectAfterSwitch: 'tags'},
      component: () => import('@/components/setup/TagForm')
    }, {
      name: 'forms',
      path: '/setup/forms',
      component: () => import('@/components/setup/FormList')
    }, {
      name: 'forms-add',
      path: '/setup/forms/add',
      component: () => import('@/components/setup/FormForm')
    }, {
      name: 'forms-edit',
      path: '/setup/forms/:id',
      meta: {redirectAfterSwitch: 'forms'},
      component: () => import('@/components/setup/FormForm')
    }, {
      name: 'af-forms',
      path: '/autofixes/form-field-notifications',
      component: () => import('@/components/autofixes/Campaigns')
    }, {
      name: 'af-forms-add',
      path: '/autofixes/form-field-notifications/add',
      component: () => import('@/components/autofixes/CampaignEditor'),
      beforeEnter: (to, from, next) => {
        if (!to.query?.template) {
          return next({ name: 'af-library' })
        }
        next()
      }
    }, {
      name: 'af-forms-edit',
      path: '/autofixes/form-field-notifications/:id',
      meta: {redirectAfterSwitch: 'af-forms'},
      component: () => import('@/components/autofixes/CampaignEditor')
    }, {
      name: 'af-library',
      path: '/autofixes/library',
      component: () => import('@/components/autofixes/Library')
    }, {
      name: 'af-templates',
      path: '/autofixes/templates',
      component: () => import('@/components/autofixes/Templates')
    }, {
      name: 'af-templates-add',
      path: '/autofixes/templates/add',
      component: () => import('@/components/autofixes/TemplateEditor')
    }, {
      name: 'af-templates-edit',
      path: '/autofixes/templates/:id',
      meta: {redirectAfterSwitch: 'af-templates'},
      component: () => import('@/components/autofixes/TemplateEditor')
    }, {
      name: 'st-forms',
      path: '/smart-tooltips/form-field-notifications',
      component: () => import('@/components/smart-tooltips/Campaigns')
    }, {
      name: 'st-forms-add',
      path: '/smart-tooltips/form-field-notifications/add',
      component: () => import('@/components/smart-tooltips/CampaignEditor'),
      beforeEnter: (to, from, next) => {
        if (!to.query?.template) {
          return next({ name: 'st-library' })
        }
        next()
      }
    }, {
      name: 'st-forms-edit',
      path: '/smart-tooltips/form-field-notifications/:id',
      meta: {redirectAfterSwitch: 'st-forms'},
      component: () => import('@/components/smart-tooltips/CampaignEditor')
    }, {
      name: 'st-library',
      path: '/smart-tooltips/library',
      component: () => import('@/components/smart-tooltips/Library')
    }, {
      name: 'st-templates',
      path: '/smart-tooltips/templates',
      component: () => import('@/components/smart-tooltips/Templates')
    }, {
      name: 'st-templates-add',
      path: '/smart-tooltips/templates/add',
      component: () => import('@/components/smart-tooltips/TemplateEditor')
    }, {
      name: 'st-templates-edit',
      path: '/smart-tooltips/templates/:id',
      meta: {redirectAfterSwitch: 'st-templates'},
      component: () => import('@/components/smart-tooltips/TemplateEditor')
    }, {
      name: 'performance-alerts-checks',
      path: '/performance-alerts/checks',
      component: () => import('@/components/performance-alerts/Overview')
    }, {
      name: 'performance-alerts-notifications',
      path: '/performance-alerts/notifications',
      component: () => import('@/components/performance-alerts/Notifications')
    }, {
      name: 'notification-channels',
      path: '/performance-alerts/notification-channels',
      component: () => import('@/components/performance-alerts/NotificationChannels')
    }, {
      name: 'integrations-partners',
      path: '/integrations/partners',
      component: () => import('@/components/integrations/Partners')
    }, {
      name: 'integrations',
      path: '/integrations',
      component: () => import('@/components/integrations/IntegrationList.vue')
    }, {
      name: 'integrations-shopify-request',
      path: '/integrations/third-party/shopify/request',
      component: () => import('@/components/integrations/IntegrationList.vue')
    }, {
      name: 'integrations-shopify-authorised',
      path: '/integrations/third-party/shopify/authorised',
      component: () => import('@/components/integrations/IntegrationList.vue')
    }, {
      name: 'integrations-shopify-subscription',
      path: '/integrations/third-party/shopify/subscription',
      component: () => import('@/components/integrations/IntegrationList.vue')
    }, {
      name: 'websites',
      path: '/administration/websites',
      component: () => import('@/components/administration/WebsiteList')
    }, {
      name: 'websites-add',
      path: '/administration/websites/add',
      component: () => import('@/components/administration/WebsiteForm')
    }, {
      name: 'websites-add-prefilled',
      path: '/administration/websites/add/:website',
      component: () => import('@/components/administration/WebsiteForm')
    }, {
      name: 'websites-edit',
      path: '/administration/websites/:id',
      component: () => import('@/components/administration/WebsiteForm')
    }, {
      name: 'companies',
      path: '/administration/companies',
      component: () => import('@/components/administration/CompanyList')
    }, {
      name: 'companies-add',
      path: '/administration/companies/add',
      component: () => import('@/components/administration/CompanyForm')
    }, {
      name: 'companies-edit',
      path: '/administration/companies/:id',
      component: () => import('@/components/administration/CompanyForm')
    }, {
      name: 'users',
      path: '/administration/users',
      component: () => import('@/components/administration/UserList')
    }, {
      name: 'users-add',
      path: '/administration/users/add',
      component: () => import('@/components/administration/UserForm')
    }, {
      name: 'users-edit',
      path: '/administration/users/:id',
      component: () => import('@/components/administration/UserForm')
    }, {
      name: 'admin-tools',
      path: '/administration/admin-tools',
      component: () => import('@/components/administration/AdminTools.vue')
    }, {
      name: 'admin-tools-forms-bulk-management',
      path: '/administration/admin-tools/forms-bulk-management',
      component: () => import('@/components/administration/tools/BulkFormManagement.vue')
    }, {
      name: 'admin-tools-queries-exit-urls',
      path: '/administration/admin-tools/queries/exit-urls',
      component: () => import('@/components/administration/tools/queries/ExitUrls.vue')
    }, {
      name: 'admin-tools-queries-entry-urls',
      path: '/administration/admin-tools/queries/entry-urls',
      component: () => import('@/components/administration/tools/queries/EntryUrls.vue')
    }, {
      name: 'admin-tools-queries-brooks-placeordererror',
      path: '/administration/admin-tools/queries/brooks/placeordererror',
      component: () => import('@/components/administration/tools/queries/brooks/PlaceOrderError.vue')
    }]
  }, {
    path: '/login',
    component: LayoutAuth,
    children: [{
      name: 'login',
      path: '',
      component: Login
    }, {
      path: ':email',
      component: Login
    }]
  }, {
    path: '/auth/google',
    component: LayoutAuth,
    children: [{
      name: 'google-auth-callback',
      path: 'callback',
      component: () => import('@/components/auth/GoogleAuthCallback.vue')
    }]
  }, {
    path: '/',
    component: LayoutAuth,
    children: [{
      name: 'signup',
      path: '/signup',
      component: () => import('@/components/Signup')
    }, {
      name: 'signup',
      path: '/signup/:email',
      component: () => import('@/components/Signup')
    }]
  }, {
    path: '/signupEmbed',
    component: () => import('@/components/Signup')
  }, {
    path: '/subscription/',
    component: LayoutAuth,
    children: [{
      name: 'subscription',
      path: '/subscription/welcome',
      component: () => import('@/components/SubscriptionProcessor')
    }]
  }, {
    path: '/password-reset/',
    component: LayoutAuth,
    children: [{
      name: 'password-reset',
      path: '',
      component: () => import('@/components/PasswordReset')
    }, {
      name: 'password-reset-email',
      path: ':email',
      component: () => import('@/components/PasswordReset')
    }, {
      name: 'password-reset-end',
      path: ':email/:token',
      component: () => import('@/components/PasswordResetEnd')
    }]
  }, {
    // 404 Not Found page
    path: '*',
    component: NotFound
  }]
})

async function ensureOnboardingAccountContext (to) {
  const app = router.app || Vue.prototype.$api?.mainApp
  const api = app?.$api || Vue.prototype.$api
  if (!app || !api) return null
  if (typeof api.getToken === 'function' && !api.getToken()) return null
  if (typeof api.isLoginTokenExpired === 'function' && api.isLoginTokenExpired()) return null

  if (!app.$root.me) {
    const response = await api.get('/me')
    app.$root.me = response.data
    app.$root.me.initialRoles = app.$root.me.roles
  }

  if (app.$root.client) return app.$root.client

  const grants = app.$root?.me?.grants?.combined || []
  const requestedClientHash = to.query.client_hash || sessionStorage.client_hash || localStorage.client_hash
  const client = grants.find(grant => grant.client_hash === requestedClientHash) || grants[0] || null
  if (!client) return null

  app.$root.client = client
  sessionStorage.client_hash = localStorage.client_hash = client.client_hash
  return client
}

async function onboardingRouteForNavigation (to) {
  const app = router.app || Vue.prototype.$api?.mainApp
  const api = app?.$api || Vue.prototype.$api
  if (!api) return null

  const client = await ensureOnboardingAccountContext(to)
  if (!client?.client_hash) return null

  // Skip the onboarding status round-trip for clients that are not in the onboarding flow.
  // Onboarding-required clients carry the 'Onboarding required' client property, which is loaded
  // asynchronously, so we only trust this shortcut once the property set is present.
  if (Array.isArray(client.properties) && !isClientOnboardingRequired(client)) return null

  const status = await getOnboardingStatus(api, client.client_hash)
  const route = onboardingRouteForStatus(status, to.name)
  if (!route) return null

  return {
    ...route,
    query: to.query.client_hash ? { client_hash: to.query.client_hash } : {}
  }
}

function shouldGuardOnboarding (to) {
  return to.matched.some(route => route.meta?.guardOnboarding)
}

router.afterEach(() => {
  // i18n
  if (typeof router?.currentRoute?.query?.locale !== 'undefined' && Object.keys(router.app.$root.i18n.messages).includes(router.currentRoute.query.locale)) {
    sessionStorage.setItem('locale', router.currentRoute.query.locale)
    router.app.$root.i18n.locale = router.currentRoute.query.locale
  }

  // On small screens collapse sidenav
  if (window.layoutHelpers && window.layoutHelpers.isSmallScreen() && !window.layoutHelpers.isCollapsed()) {
    setTimeout(() => window.layoutHelpers.setCollapsed(true, true), 10)
  }

  // Scroll to top of the page
  globals().scrollTop(0, 0)

  // Subscription expired
  if (router.app.$root && router.app.$root.me && router.app.$root.setupStatusStep === 1000) {
    if (!router.app.$root.hasRole('admin.internal')) {
      if (router.currentRoute.name !== 'account') {
        router.push({ name: 'account' })
        router.app.$root.analyticsEvent('trialEnded')
      }

      // Limit UI functions to the finance role
      router.app.$root.clearRoles()
    } else {
      console.warn('[admin.internal] Normally I would redirect to the account page as the subscription expired')
    }
  } else {
    router.app.$root.setupStatusStep = null
  }

  router.app.$api.conditionallyRefreshToken()

  // Remove debug info from the menu
  typeof router.app.$root.clearDebugDataFromMenu === 'function' && router.app.$root.clearDebugDataFromMenu()

  if (router.app?.$root?.client?.name) {
    router.app?.analyticsEvent('website', { website: router.app.$root.client.name })
  }

  // AI Chat: reset state on pages where we do not offer a chat
  try {
    const pagesWithChat = ['dashboard', 'onboarding', 'eureka', 'eureka-analysis', 'form-insights', 'form-insights-for-form', 'field-metrics', 'field-metrics-for-form', 'session-replays', 'session-replays-for-form', 'forms', 'tags',]
    if (!pagesWithChat.includes(router.currentRoute.name) && typeof router.app?.$root?.clearAiChatMetadata === 'function') {
      console.log('🧹 Clearing the AI Chat metadata')
      router.app.$root.clearAiChatMetadata()
    }
  } catch (e) {
    console.error('🧹 Clearing the AI Chat metadata', e)
    /* no-op */
  }
})

router.beforeEach(async (to, from, next) => {
  // Set loading state
  document.body.classList.add('app-loading')

  document.body.dispatchEvent(new CustomEvent('vueRouterBeforeEach', {
    detail: { to, from, next }
  }))

  if (shouldGuardOnboarding(to)) {
    try {
      const onboardingRoute = await onboardingRouteForNavigation(to)
      if (onboardingRoute) {
        next(onboardingRoute)
        return
      }
    } catch (e) {
      console.error('Unable to resolve onboarding entrypoint', e)
    }
  }

  // Add tiny timeout to finish page transition
  setTimeout(() => next(), 10)
})

export default router
